// Story 023 — Draw control + status pill.
//
// ANA-R3-GEOM-03 (OQ1 ✅) — the single draw toggle is replaced by a SEGMENTED
// CONTROL `Rectangle | Polygon | Polyline`. Selecting a mode turns draw mode on
// (and sets the active mode); clicking the already-selected mode toggles draw
// off. Hotkeys (R / P / L toggle a mode, D toggles the last mode) are owned by
// the global listener in `DrawOverlay`, which also reads `drawStore`. A
// per-mode status hint tells the analyst how to finish the shape.
//
// SB6-ANA-VIEW-05 — rendered inside the draggable `ViewerToolbar`, which owns
// position + z-index. `editAllowed` is false on LOCKED / SIGNED_OFF data sets:
// the whole control is shown DISABLED with an "Editing locked" tooltip (Q2=b).
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDrawStore, type DrawMode } from "../state/drawStore.js";
import {
  ChartLineIcon,
  InfoIcon,
  PentagonIcon,
  SquareIcon,
} from "@aeriosiq/ui/icons";

export interface DrawToolbarProps {
  /** False on LOCKED / SIGNED_OFF — control shown disabled, not hidden (Q2=b). */
  editAllowed: boolean;
}

/** Per-mode status hint (GEOM-03 design). */
const MODE_HINT: Record<DrawMode, string> = {
  rect: "Drag to draw · Esc to cancel",
  polygon:
    "Click to add points · Enter or right-click to close · Esc to cancel",
  polyline:
    "Click to add points · Enter, double-click or right-click to finish · Esc to cancel",
};

export function DrawToolbar({
  editAllowed,
}: DrawToolbarProps): React.ReactElement {
  const enabled = useDrawStore((s) => s.enabled);
  const mode = useDrawStore((s) => s.mode);
  const setMode = useDrawStore((s) => s.setMode);
  const setEnabled = useDrawStore((s) => s.setEnabled);
  // Draw can only be "on" when editing is allowed; a locked DA shows the
  // control inert regardless of any stale store state.
  const active = enabled && editAllowed;

  // ToggleButtonGroup is "exclusive": the selected value is the active mode
  // when drawing, else null (no mode highlighted while draw is off).
  const value: DrawMode | null = active ? mode : null;

  const handleChange = (
    _e: React.MouseEvent<HTMLElement>,
    next: DrawMode | null,
  ) => {
    if (!editAllowed) return;
    if (next === null) {
      // Clicking the already-selected mode → toggle draw off.
      setEnabled(false);
      return;
    }
    setMode(next);
    if (!enabled) setEnabled(true);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip
        title={
          editAllowed
            ? "Draw a new defect — Rectangle (R) · Polygon (P) · Polyline (L)"
            : "Editing locked"
        }
      >
        {/* span wrapper so the Tooltip still fires over a disabled control */}
        <span>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value}
            onChange={handleChange}
            disabled={!editAllowed}
            aria-label="Draw new defect"
            data-testid="draw-mode-group"
            sx={{
              "& .MuiToggleButton-root": {
                borderColor: "divider",
                color: "text.primary",
                px: 1,
              },
              "& .Mui-selected": {
                backgroundColor: "primary.main",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "primary.dark" },
              },
            }}
          >
            <ToggleButton
              value="rect"
              aria-label="Rectangle"
              aria-pressed={active && mode === "rect"}
              data-testid="draw-mode-rect"
            >
              <SquareIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="polygon"
              aria-label="Polygon"
              aria-pressed={active && mode === "polygon"}
              data-testid="draw-mode-polygon"
            >
              <PentagonIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="polyline"
              aria-label="Polyline"
              aria-pressed={active && mode === "polyline"}
              data-testid="draw-mode-polyline"
            >
              <ChartLineIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </span>
      </Tooltip>
      {active ? (
        <Typography
          variant="caption"
          sx={{
            color: "primary.main",
            fontWeight: 600,
            backgroundColor: "background.paper",
            borderRadius: 1,
            px: 1,
            py: 0.25,
            boxShadow: 1,
            whiteSpace: "nowrap",
          }}
          role="status"
          aria-live="polite"
          data-testid="draw-mode-hint"
        >
          {MODE_HINT[mode]}
        </Typography>
      ) : null}
      {/* ANA-R4-POLY-01 — gesture help, shown in polygon/polyline mode. */}
      {active && mode !== "rect" ? (
        <Tooltip
          arrow
          title={
            <Box sx={{ p: 0.5, lineHeight: 1.6 }}>
              <strong>Editing a shape</strong>
              <br />• Add a point: click on a line
              <br />• Remove a point: select it, then Delete
              <br />• Move the whole shape: right-click-drag
              <br />• Close / finish: Enter or a single right-click
            </Box>
          }
        >
          <IconButton
            size="small"
            aria-label="Polygon editing help"
            data-testid="draw-polygon-help"
            sx={{ color: "text.secondary" }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
